<template>
  <div class="cart-bg">
    <div class="cart-main-box">
      <div class="cart-bar">
        <h2>{{ translations.cart.title }}</h2>
        <router-link to="/" class="cart-back-btn">&larr; {{ translations.cart.back_to_store }}</router-link>
      </div>

      <div v-if="cartItems.length === 0" class="cart-empty">{{ translations.cart.empty }}</div>

      <div v-else>
        <div
          v-for="item in cartItems"
          :key="`${item.productId}-${item.variantId ?? 'no-variant'}`"
          class="cart-table-row"
        >
          <div class="col col-product">
            <div class="prod-info">
              <img :src="item.image" alt="" />
              <div class="prod-col-text">
                <div class="prod-title">{{ item.name }}</div>
                <div class="prod-details" v-if="item.variant">
                  {{ translations.cart.variant }}: {{ getVariantName(item.variant) }}
                </div>
              </div>
            </div>
          </div>

          <div class="col col-price">
            <div class="prod-price">
              <b>{{ item.price.toFixed(2) }} {{ getCurrencySymbol(currentCurrency) }}</b>
            </div>
          </div>

          <div class="col col-qty">
            <div class="qty-field">
              <button @click="decreaseQty(item.productId, item.variantId, item)">-</button>
              <span>{{ item.quantity }}</span>
              <button @click="increaseQty(item.productId, item.variantId, item)">+</button>
            </div>
          </div>

          <div class="col col-total">
            <b>{{ (item.price * item.quantity).toFixed(2) }} {{ getCurrencySymbol(currentCurrency) }}</b>
          </div>

          <div class="col col-empty">
            <button class="remove-btn" @click="removeItem(item.productId, item.variantId, item)">×</button>
          </div>
        </div>

        <!-- Banner z licznikiem do darmowej wysyłki -->
<div class="free-shipping-banner" v-if="!freeShippingActive && remainingForFreeShipping > 0">
  🚚 {{ translations.cart?.remaining_before || "Brakuje Ci" }}
  <b>{{ remainingForFreeShipping.toFixed(2) }} {{ getCurrencySymbol(currentCurrency) }}</b>
  {{ translations.cart?.remaining_after || "do darmowej wysyłki" }}!
</div>
<div class="free-shipping-banner success" v-else-if="freeShippingActive">
  🎉 {{ translations.cart?.free_shipping_active || "Masz darmową wysyłkę!" }}
</div>
        

        <div class="cart-summary">
          <div class="sum-row">
            <label for="country">{{ translations.cart.select_country }}</label>
            <select v-model="selectedCountry" id="country" class="country-select">
              <option v-for="c in availableCountries" :key="c.code" :value="c.code">
                {{ countryEndonym(c.code) }}
              </option>
            </select>
          </div>

          <div class="sum-row">
            <span>{{ translations.cart.delivery }}:</span>
            <span>
              <b>{{ shippingTotal.toFixed(2) }} {{ getCurrencySymbol(currentCurrency) }}</b>
              <span v-if="freeShippingActive" class="free-shipping-note">({{ translations.cart?.free_shipping_note || "darmowa wysyłka" }})</span>
            </span>
          </div>

          <div class="sum-row">
            <span>{{ translations.cart.total }}:</span>
            <span><b>{{ total.toFixed(2) }} {{ getCurrencySymbol(currentCurrency) }}</b></span>
          </div>

          <div class="sum-row total-final">
            <span>{{ translations.cart.total_final }}:</span>
            <span><b>{{ (total + shippingTotal).toFixed(2) }} {{ getCurrencySymbol(currentCurrency) }}</b></span>
          </div>

          <!-- trackujemy kliknięcie checkoutu zanim przejdziemy dalej -->
          <router-link to="/podsumowanie" custom v-slot="{ navigate }">
            <button @click="onCheckout(navigate)" class="checkout-btn">
              {{ translations.cart.go_to_checkout }}
            </button>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import { translations, currentLang } from '@/locales/lang'
import { currentCurrency } from '@/composables/currency'
import eventBus from '@/eventBus.js' // mitt()

function getCurrencySymbol(currency) {
  const symbols = { PLN: 'zł', EUR: '€', USD: '$' }
  return symbols[currency] || currency
}

export default {
  name: 'AppCart',
  data() {
    return {
      cartItems: [],
      selectedCountry: 'PL',
      availableCountries: [],

      // Darmowa wysyłka od 100 (możesz zmienić poniżej)
      freeShippingThresholds: {
        PLN: 200, EUR: 50, USD: 55, CZK: 1200, HUF: 20000,
        BGN: 100, RON: 250, DKK: 370, SEK: 550
      },
      defaultFreeShippingThreshold: 99999
    }
  },
  computed: {
    translations() {
      return translations.value
    },
    currentLanguage() { return currentLang.value },
    currentCurrency() {
      return currentCurrency.value || 'PLN'
    },
    total() {
      return this.cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0)
    },
    currentFreeShippingThreshold() {
      return this.freeShippingThresholds[this.currentCurrency] ?? this.defaultFreeShippingThreshold
    },
    freeShippingActive() {
      // Darmowa wysyłka jeśli: przekroczono próg LUB jakiś produkt ma darmową wysyłkę
      if (this.total >= this.currentFreeShippingThreshold) return true
      return this.cartItems.some(item => {
        const opt = item.variant?.shipping_options?.find(o => o.country === this.selectedCountry)
        if (!opt) return false
        const priceKey = 'price_' + (this.currentCurrency || 'PLN').toLowerCase()
        return Number(opt[priceKey] || 0) === 0
      })
    },
    remainingForFreeShipping() {
      const remaining = this.currentFreeShippingThreshold - this.total
      return remaining > 0 ? remaining : 0
    },
    // Jedna stawka: najwyższa cena dostawy spośród pozycji (bez mnożenia przez ilość)
    shippingTotal() {
      if (this.freeShippingActive) return 0

      let maxShipping = 0
      for (const item of this.cartItems) {
        const opt = item.variant?.shipping_options?.find(o => o.country === this.selectedCountry)
        if (!opt) continue

        let shippingPrice = 0
        switch (this.currentCurrency) {
          case 'EUR': shippingPrice = parseFloat(opt.price_eur) || 0; break
          case 'USD': shippingPrice = parseFloat(opt.price_usd) || 0; break
          case 'CZK': shippingPrice = parseFloat(opt.price_czk) || 0; break
          case 'HUF': shippingPrice = parseFloat(opt.price_huf) || 0; break
          case 'BGN': shippingPrice = parseFloat(opt.price_bgn) || 0; break
          case 'RON': shippingPrice = parseFloat(opt.price_ron) || 0; break
          case 'DKK': shippingPrice = parseFloat(opt.price_dkk) || 0; break
          case 'SEK': shippingPrice = parseFloat(opt.price_sek) || 0; break
          default:    shippingPrice = parseFloat(opt.price_pln) || 0; break
        }

        if (shippingPrice > maxShipping) maxShipping = shippingPrice
      }
      return maxShipping
    }
  },
  watch: {
    currentCurrency() { this.loadCart() },
    availableCountries(newVal) {
      if (newVal.length > 0) {
        const stored = localStorage.getItem('ship_to_iso')
        if (stored && newVal.find(c => c.code === stored)) {
          this.selectedCountry = stored
        }
      }
    },
    selectedCountry() { return }
  },
  async mounted() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    window.addEventListener('languageChanged', this.loadCart)
    window.addEventListener('currencyChanged', this.loadCart)
    await this.loadCart()
    this.syncHeaderBadge()
    this.trackViewCart() // GA4 view_cart + Pixel ViewContent(cart)
  },
  beforeUnmount() {
    window.removeEventListener('languageChanged', this.loadCart)
    window.removeEventListener('currencyChanged', this.loadCart)
  },
  methods: {
    getCurrencySymbol,

    /* ===== Helpers: GA4 & Pixel ===== */
    gaAvailable() { return typeof window !== 'undefined' && typeof window.gtag === 'function' },
    fbqAvailable() { return typeof window !== 'undefined' && typeof window.fbq === 'function' },
    gaEvent(name, payload) {
      try {
        if (this.gaAvailable()) window.gtag('event', name, payload)
      } catch (e) { /* noop */ }
    },
    fbqTrackCustom(name, payload) {
      try {
        if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
          window.fbq('trackCustom', name, payload)
        }
      } catch(e) { void e }
    },
    fbqTrack(name, payload) {
      try {
        if (this.fbqAvailable()) window.fbq('track', name, payload)
      } catch (e) { /* noop */ }
    },
    gaItems() {
      // GA4 items: id, name, quantity, price, item_variant (opcjonalnie)
      return this.cartItems.map(i => ({
        item_id: String(i.productId),
        item_name: i.name,
        quantity: Number(i.quantity) || 1,
        price: Number(i.price) || 0,
        item_variant: i.variant?.type?.name || undefined
      }))
    },
    pixelContents() {
      // Pixel contents: id, quantity, item_price
      return this.cartItems.map(i => ({
        id: String(i.productId),
        quantity: Number(i.quantity) || 1,
        item_price: Number(i.price) || 0
      }))
    },

    trackViewCart() {
      // GA4: view_cart
      this.gaEvent('view_cart', {
        currency: this.currentCurrency,
        value: Number(this.total) || 0,
        items: this.gaItems()
      })
      // Pixel: użyjemy ViewContent z content_type 'cart'
      const vcEventId = this.generateEventId('vc')
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'ViewContent', {
          content_type: 'cart',
          currency: this.currentCurrency,
          value: Number(this.total) || 0,
          contents: this.pixelContents()
        }, { eventID: vcEventId })
      }
    },

    generateEventId(prefix) {
      return prefix + '_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
    },
    trackAddToCartOne(item) {
      // GA4: add_to_cart (dla +1)
      this.gaEvent('add_to_cart', {
        currency: this.currentCurrency,
        value: Number(item.price) || 0,
        items: [{
          item_id: String(item.productId),
          item_name: item.name,
          quantity: 1,
          price: Number(item.price) || 0,
          item_variant: item.variant?.type?.name || undefined
        }]
      })
      // Pixel: AddToCart
      const atcEventId = this.generateEventId('atc')
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'AddToCart', {
          content_type: 'product',
          currency: this.currentCurrency,
          value: Number(item.price) || 0,
          contents: [{ id: String(item.productId), quantity: 1, item_price: Number(item.price) || 0 }]
        }, { eventID: atcEventId })
      }
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'add_to_cart', {
          currency: this.currentCurrency,
          value: Number(item.price) || 0,
          items: [{ item_id: String(item.productId), item_name: item.name, quantity: 1, price: Number(item.price) || 0 }]
        })
      }
    },

    trackRemoveFromCartOne(item, qty = 1) {
      // GA4: remove_from_cart
      this.gaEvent('remove_from_cart', {
        currency: this.currentCurrency,
        value: Number(item.price) * qty || 0,
        items: [{
          item_id: String(item.productId),
          item_name: item.name,
          quantity: qty,
          price: Number(item.price) || 0,
          item_variant: item.variant?.type?.name || undefined
        }]
      })
      // Pixel: RemoveFromCart
      this.fbqTrackCustom('RemoveFromCart', {
        content_type: 'product',
        currency: this.currentCurrency,
        value: Number(item.price) * qty || 0,
        contents: [{ id: String(item.productId), quantity: qty, item_price: Number(item.price) || 0 }]
      })
    },

    onCheckout(navigate) {
      // GA4: begin_checkout
      this.gaEvent('begin_checkout', {
        currency: this.currentCurrency,
        value: Number(this.total + this.shippingTotal) || 0,
        coupon: undefined,
        items: this.gaItems()
      })
      // Pixel: InitiateCheckout
      this.fbqTrack('InitiateCheckout', {
        currency: this.currentCurrency,
        value: Number(this.total + this.shippingTotal) || 0,
        contents: this.pixelContents(),
        num_items: this.cartItems.reduce((s, i) => s + (Number(i.quantity) || 0), 0)
      })
      // przejście dalej
      navigate()
    },

    /* ===== Core koszyka ===== */
    async loadCart() {
      let rawCart = []
      try {
        rawCart = JSON.parse(localStorage.getItem('cart') || '[]')
      } catch (e) {
        rawCart = []
      }

      rawCart = rawCart.filter(e => e && e.slug && e.id)

      const currencyCode = this.currentCurrency
      const updated = await Promise.all(
        rawCart.map(async (entry) => {
          try {
            const url = `/api/v1/products/${entry.slug}/`
            const res = await fetch(url, { headers: { 'Accept-Language': currentLang.value } })
            if (!res.ok) throw new Error('API error')
            const product = await res.json()

            let variant = null
            let price = parseFloat(product.price)
            let variantId = entry.variantId ?? null

            if (variantId && product.variants) {
              variant = product.variants.find(v => Number(v.id) === Number(variantId)) || null
              if (variant) {
                const found = variant.prices?.find(p => p.currency?.code === currencyCode)
                if (found) price = parseFloat(found.amount)
              }
            }

            return {
              productId: product.id,
              slug: product.slug,
              name: product.name,
              image: product.get_image || product.get_thumbnail,
              variant,
              variantId: variant?.id ?? null,
              price: price,
              quantity: Number(entry.quantity) || 1
            }
          } catch (err) {
            console.error('Błąd ładowania produktu:', err)
            return null
          }
        })
      )

      this.cartItems = updated.filter(Boolean)

      const countriesSet = new Set()
      this.cartItems.forEach(item => {
        item.variant?.shipping_options?.forEach(opt => {
          if (opt.country) {
            const code = String(opt.country).toUpperCase()
            countriesSet.add(JSON.stringify({ code: code, name: code }))
          }
        })
      })
      this.availableCountries = Array.from(countriesSet).map(j => JSON.parse(j))
      // Auto-wykryj kraj na podstawie IP
      try {
        const geo = await fetch('https://freeipapi.com/api/json')
        const geoData = await geo.json()
        const countryCode = geoData.countryCode || 'PL'
        if (this.availableCountries.find(c => c.code === countryCode)) {
          this.selectedCountry = countryCode
        }
      } catch(e) {
        // fallback - zostaw PL
      }

      this.syncHeaderBadge()
    },

    updateLocalStorage() {
      const raw = this.cartItems.map(item => ({
        id: item.productId,
        slug: item.slug,
        variantId: item.variantId ?? null,
        quantity: item.quantity
      }))
      localStorage.setItem('cart', JSON.stringify(raw))
      this.syncHeaderBadge()
    },


    getVariantName(variant) {
      if (!variant) return ''
      const lang = this.currentLanguage || 'pl'
      return variant['value_'+lang] || variant.value || (variant.type && variant.type.name) || ''
    },
    regionToLang(code) {
      const m = {
        PL:'pl', DE:'de', AT:'de', BE:'nl', BG:'bg', HR:'hr', CZ:'cs', DK:'da', EE:'et',
        ES:'es', FR:'fr', NL:'nl', LT:'lt', LV:'lv', RO:'ro', SK:'sk', SI:'sl',
        SE:'sv', IT:'it', HU:'hu', PT:'pt', IE:'en'
      }
      return m[String(code).toUpperCase()] || 'en'
    },
    countryEndonym(iso) {
      const lang = this.regionToLang(iso)
      try {
        const dn = new Intl.DisplayNames([lang], { type: 'region' })
        return dn.of(String(iso).toUpperCase()) || String(iso).toUpperCase()
      } catch { return String(iso).toUpperCase() }
    },
    syncHeaderBadge() {
      const count = this.cartItems.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0)
      localStorage.setItem('cart_count', String(count))
      try {
        eventBus?.emit?.('cartUpdated', { count })
      } catch (e) {
        console.debug('cartUpdated emit failed:', e)
      }
      window.dispatchEvent(new CustomEvent('cart:updated', { detail: { count } }))
    },

    increaseQty(productId, variantId, itemRef) {
      const idx = this.cartItems.findIndex(i => i.productId === productId && (i.variantId ?? null) === (variantId ?? null))
      if (idx !== -1) {
        this.cartItems[idx].quantity++
        this.updateLocalStorage()
        // track +1
        this.trackAddToCartOne(itemRef || this.cartItems[idx])
      }
    },
    decreaseQty(productId, variantId, itemRef) {
      const idx = this.cartItems.findIndex(i => i.productId === productId && (i.variantId ?? null) === (variantId ?? null))
      if (idx !== -1 && this.cartItems[idx].quantity > 1) {
        this.cartItems[idx].quantity--
        this.updateLocalStorage()
        // track -1
        this.trackRemoveFromCartOne(itemRef || this.cartItems[idx], 1)
      }
    },
    removeItem(productId, variantId, itemRef) {
      const found = this.cartItems.find(i => i.productId === productId && (i.variantId ?? null) === (variantId ?? null))
      if (found) {
        // track full removal
        this.trackRemoveFromCartOne(itemRef || found, Number(found.quantity) || 1)
      }
      this.cartItems = this.cartItems.filter(i => !(i.productId === productId && (i.variantId ?? null) === (variantId ?? null)))
      this.updateLocalStorage()
    }
  }
}
</script>

<style scoped>
.free-shipping-banner {
  margin: 1rem 0;
  padding: 0.75rem 1rem;
  background: #f0f8ff;
  border: 1px solid #cde;
  border-radius: 10px;
  text-align: center;
  font-weight: 500;
}
.free-shipping-banner.success {
  background: #d4f9d2;
  border-color: #8fd48d;
  color: #205c20;
}
.free-shipping-note {
  color: #28a745;
  font-size: 0.9em;
  margin-left: 4px;
}
</style>
